import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RequestMacroDialogProps {
  searchQuery: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RequestMacroDialog = ({ searchQuery, open, onOpenChange }: RequestMacroDialogProps) => {
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await supabase.functions.invoke('submit-macro-request', {
        body: {
          search_query: searchQuery,
          additional_notes: additionalNotes || null,
        },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (response.error) {
        const errorData = response.error;
        if (errorData.message?.includes('429') || errorData.message?.includes('Too many')) {
          toast.error("Too many requests. Please wait a few minutes before trying again.");
        } else {
          throw new Error(errorData.message || 'Failed to submit request');
        }
        return;
      }

      toast.success("Request submitted! We'll review it soon.");
      setAdditionalNotes("");
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Request a Macro</DialogTitle>
          <DialogDescription>
            Tell us what you're looking for and we'll consider adding it to MacroVault.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">You searched for:</label>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">{searchQuery}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="notes" className="text-sm font-medium">
              Additional details (optional)
            </label>
            <Textarea
              id="notes"
              placeholder="Tell us more about what this macro should do..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
